import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-workspace-tabs>` says about itself.
 *
 * IT IS THE ROW, NOT THE THINGS AT ITS ENDS. Left to right the band holds: a slot the App Launcher
 * projects into, the current app's name, a divider, the persistent AI-home anchor, the app's own
 * tabs, and a slot the Customize Navigation gadget projects into at the far end. The two ends are
 * separate components — this row provides the seams and stays out of their business, which is why
 * two of the examples below place real pieces into those slots rather than describing them.
 *
 * A CARET ON A TAB IS ONLY THE REPORT. An object tab that carries `caret: true` draws a small
 * chevron beside its label, and pressing it raises `tab-caret` with the object and the caret's live
 * screen rectangle. It opens nothing. The shell owns one `<caos-object-caret>`, fetches that
 * object's recents and list views, and opens that popover at the rectangle it was handed. With
 * nobody listening the carets draw and do nothing, hiding the more interesting half of the row — so
 * the first example declares a host that plays the shell, using the same popover component the
 * ObjectCaret entry shows.
 *
 * THE HOME ANCHOR IS NOT ONE OF THE TABS. It is built by the row itself, is always the leading tab,
 * and raises its own event: it goes to a global home WITHOUT changing which app is active. So the
 * `tabs` list an app hands over starts at the app's own destinations.
 *
 * AND NEITHER ARE THE PINNED TABS. `homeTabs` is a second, separate list drawn between the anchor
 * and the app's tabs. The difference is not how they look — it is that the app's tabs are replaced
 * wholesale every time the app changes and these are not, so a destination in this list stays
 * reachable from everywhere. Who is allowed one, and how many there may be, is decided well
 * upstream of this row; it draws the list it is handed.
 */
const docs: ComponentDocs = {
  aliases: ['nav bar', 'object nav', 'app tabs', 'tab row', 'navigation bar', 'main nav'],

  answers: [
    'move between the destinations of the app I am in',
    'show which object I am currently looking at',
    'reach an object’s list views without loading its list',
    'keep a long tab set from overrunning the bar',
    'show which app is open',
  ],

  // The frame's own second band. The tabs are already scoped to the active app, so a copy of this
  // row inside a page would be a second, competing claim about where a person is.
  placements: ['seam'],

  inputs: [
    {
      name: 'tabs',
      // No `attribute`: a list of tabs cannot ride an attribute. It is set as a property.
      type: 'json',
      description:
        'The active app’s destinations, in order, as `{ id, label, href, active?, object?, caret?, icon?, ' +
        'overflow? }`. `object` marks it as an object tab — its label is set uppercase and the key rides the ' +
        'caret event. `caret` draws the per-tab chevron. `icon` names a glyph from the shared vocabulary; a ' +
        'name outside it draws no glyph rather than a blank box. `overflow` moves the tab out of the bar and ' +
        'into the trailing “More” menu — with none marked, the bar is exactly the plain bar.',
    },
    {
      name: 'homeTabs',
      // No `attribute`: a list cannot ride an attribute. It is set as a property, like `tabs`.
      type: 'json',
      description:
        'Destinations PINNED into the row, as `{ id, label, href, icon?, active? }`, drawn between the ' +
        'anchor and the app’s tabs. Unlike `tabs` they survive an app switch, which is the whole of what ' +
        'they promise — so there is no `overflow` here (a tab that hides itself when the bar is tight is ' +
        'not permanently visible) and no `object`/`caret` (these are destinations, not windows onto one ' +
        'object). Choosing one raises the same `workspace-navigate` an app tab raises.',
    },
    {
      name: 'activeAppLabel',
      attribute: 'active-app-label',
      type: 'text',
      description:
        'The name of the app that is open, drawn after the launcher in the display face so it reads as the ' +
        'title of the row. Unset ⇒ nothing is drawn there.',
    },
    {
      name: 'homeLabel',
      attribute: 'home-label',
      type: 'text',
      description:
        'What the leading anchor is called. Unset ⇒ “Home”. A shell whose home IS an app passes that ' +
        'app’s name; the anchor never names a package on its own, because whether that package is ' +
        'installed is the shell’s knowledge and not this component’s.',
      default: 'Home',
    },
    {
      name: 'homeActive',
      attribute: 'home-active',
      type: 'boolean',
      description: 'The home surface is the one on screen, so the leading anchor reads active instead of one of the tabs.',
      default: false,
    },
  ],

  events: [
    {
      name: 'workspace-home',
      when: 'The leading anchor was chosen. Go home; do NOT change which app is active.',
      carries: 'nothing',
    },
    {
      name: 'workspace-navigate',
      when: 'A tab was chosen — a pinned one in the bar, or one from the “More” menu. Both raise this, so an overflow tab activates exactly like any other.',
      carries: '`{ href, id }` — where to go, and which tab said so.',
    },
    {
      name: 'tab-caret',
      when: 'An object tab’s chevron was pressed. The row opens nothing; it says which object and where the chevron is.',
      carries: '`{ id, object, anchor: { left, bottom } }` — the caret’s live screen rectangle, ready to hand to ObjectCaret’s `openAt`.',
    },
  ],

  state: [],

  slots: [
    { name: 'launcher', takes: 'The App Launcher, at the head of the row. It stays pinned while the tab set scrolls past it.' },
    { name: 'gadget', takes: 'The Customize Navigation control, at the far end. Also pinned.' },
  ],

  tokens: [
    { token: '--caos-color-surface', controls: 'The band itself — the DATA surface, distinct from the page base the bar above it paints.' },
    { token: '--caos-color-border', controls: 'The rule under the row, the divider after the app name, and the “More” menu’s outline.' },
    { token: '--caos-color-accent', controls: 'The underline under the active tab, and the dot beside the active item in the “More” menu.' },
    { token: '--caos-color-bg', controls: 'The fill behind the active tab, and behind a “More” menu row on hover.' },
    { token: '--caos-color-text', controls: 'The app name, the active tab, and a tab under the pointer.' },
    { token: '--caos-color-text-muted', controls: 'A resting tab label and a resting caret.' },
    { token: '--caos-focus-ring', controls: 'The ring on a tab, a caret, the “More” button or a menu row reached by keyboard.' },
    { token: '--caos-font-display', controls: 'The typeface the current app’s name is set in.' },
    { token: '--caos-font-mono', controls: 'The typeface the tab labels and the “More” menu are set in.' },
    { token: '--caos-tracking-tight', controls: 'The letter-spacing of the current app’s name.' },
    { token: '--caos-radius-sm', controls: 'The corner radius of a focus ring, and of a “More” menu row.' },
    { token: '--caos-radius', controls: 'The “More” menu’s corner radius.' },
    { token: '--caos-shadow-float', controls: 'The shadow under the “More” menu.' },
    { token: '--caos-space-1', controls: 'The padding inside the “More” menu.' },
    { token: '--caos-space-2', controls: 'The space either side of the divider, and the inset at the far end before the gadget.' },
    { token: '--caos-space-3', controls: 'The gap between the launcher and the current app’s name.' },
  ],

  parts: [
    { part: 'tablist', is: 'the row of tabs' },
    { part: 'tab', is: 'one tab; the home anchor and the overflow control carry it too' },
  ],

  methods: [],

  placing:
    'It is the second band of the workspace frame, directly under the top bar and above the strip of open ' +
    'records, spanning the full width. It needs three things around it: an App Launcher projected into its ' +
    '`launcher` slot, a Customize Navigation control projected into its `gadget` slot, and a host that routes ' +
    'the three events it raises — including owning one ObjectCaret to answer `tab-caret` with.',

  whenToUse:
    'Only as the workspace frame’s app-scoped row. The tabs are already scoped by the active app, so switching ' +
    'apps swaps the whole set; nothing here is a general-purpose tab control.',

  insteadUse: [
    { name: 'TabStrip', when: 'The choices are facets of ONE record or one configuration object, inside a page.' },
    { name: 'OpenRecords', when: 'The row is the records somebody has open rather than the app’s destinations.' },
    { name: 'AppLauncher', when: 'Somebody is moving to a different app, not to a destination inside this one.' },
    { name: 'SegmentedFilter', when: 'The row changes what a list is showing rather than moving anywhere.' },
  ],

  accessibility:
    'The anchor and the tabs are a real `role="tablist"` with a roving tabindex — only the active tab is in ' +
    'the tab order, and Arrow Left/Right, Home and End move between them; the trailing “More” button joins the ' +
    'same roving set so the collapsed tabs stay reachable. The launcher sits OUTSIDE the tablist, because it is ' +
    'a menu button and not a tab. The “More” menu is a `role="menu"` that takes focus when it opens, roams with ' +
    'Up/Down, jumps with Home/End, closes on Escape returning focus to its button, and marks the active item ' +
    '`aria-current`; each per-tab caret is a button named “<tab> list views” and is out of the tab order, since ' +
    'the row is reached by arrow key. What is left to the author: the row labels itself “Workspace tabs” unless ' +
    'given an `aria-label`, and a caret’s `aria-expanded` belongs to whoever opens the popover it reports.',

  examples: [
    {
      key: 'object-tabs',
      label: 'An app’s object tabs',
      summary:
        'The row as an app hands it over: the home anchor, then the app’s objects with their glyphs and their ' +
        'per-tab carets, one of them active.',
      placement: {
        component: 'caos-workspace-tabs',
        inputs: {
          homeLabel: 'Atlantis AI',
          activeAppLabel: 'Sales Atlas',
          tabs: [
            { id: 'o:account', label: 'Accounts', href: '/sales-atlas/account', object: 'account', icon: 'building-2', caret: true, active: true },
            { id: 'o:contact', label: 'Contacts', href: '/sales-atlas/contact', object: 'contact', icon: 'users', caret: true },
            { id: 'o:invoice', label: 'Invoices', href: '/sales-atlas/invoice', object: 'invoice', icon: 'file-text', caret: true },
            { id: 'reports', label: 'Reports', href: '/sales-atlas/reports', icon: 'table-2' },
          ],
        },
        // The popover the shell owns. It renders nothing where it sits and is not projected into either
        // slot — it is simply held here so the example has one to open, exactly as the shell holds one.
        children: [
          {
            component: 'caos-object-caret',
            inputs: {
              views: [
                { id: 'v1', label: 'All Accounts', href: '/sales-atlas/account?view=all' },
                { id: 'v2', label: 'My Accounts', href: '/sales-atlas/account?view=mine' },
              ],
            },
          },
        ],
      },
      stage: { opensBelow: 196 }, // measured against the caret popover: 182px panel + 6px gap
      host: {
        summary:
          'The surrounding page is the shell: it answers a caret press by filling in that object’s recents and ' +
          'opening the popover at the rectangle the row reported.',
        run: (stage) => {
          const tabs = stage.querySelector('caos-workspace-tabs');
          const caret = stage.querySelector('caos-object-caret');
          if (!tabs || !caret) return;
          tabs.addEventListener('tab-caret', (e) => {
            const { object, anchor } = (e as CustomEvent<{ object?: string; anchor: { left: number; bottom: number } }>).detail;
            caret.recents =
              object === 'account'
                ? [
                    { id: 'a1', label: 'Meridian Supply', href: '/sales-atlas/account/a1' },
                    { id: 'a2', label: 'Calder & Finch', href: '/sales-atlas/account/a2' },
                  ]
                : [];
            caret.openAt(anchor);
          });
        },
      },
    },
    {
      key: 'composed',
      label: 'The row as the shell builds it',
      summary:
        'Both seams filled: the App Launcher pinned at the head and the Customize Navigation control pinned at ' +
        'the far end, with the tab set scrolling between them.',
      placement: {
        component: 'caos-workspace-tabs',
        inputs: {
          homeLabel: 'Atlantis AI',
          activeAppLabel: 'Sales Atlas',
          homeActive: true,
          tabs: [
            { id: 'o:account', label: 'Accounts', href: '/sales-atlas/account', object: 'account', icon: 'building-2' },
            { id: 'o:invoice', label: 'Invoices', href: '/sales-atlas/invoice', object: 'invoice', icon: 'file-text' },
          ],
        },
        children: [
          {
            component: 'caos-app-launcher',
            inputs: {
              slot: 'launcher',
              activeAppKey: 'sales-atlas',
              apps: [
                { key: 'sales-atlas', label: 'Sales Atlas', icon: 'building-2', destinationCount: 2 },
                { key: 'setup', label: 'Setup', icon: 'settings', destinationCount: 12 },
              ],
            },
          },
          {
            component: 'caos-nav-gadget',
            inputs: {
              slot: 'gadget',
              appLabel: 'Sales Atlas',
              items: [
                { id: 'account', label: 'Accounts', href: '/sales-atlas/account' },
                { id: 'invoice', label: 'Invoices', href: '/sales-atlas/invoice' },
              ],
              available: [{ id: 'order', label: 'Orders', href: '/sales-atlas/order' }],
            },
          },
        ],
      },
      // Sized for the taller of the two panels this row can open — the customize popup, measured at its
      // own entry as 448px plus an 8px gap. The launcher's panel is shorter and fits inside that.
      stage: { opensBelow: 456 },
    },
    {
      key: 'overflow',
      label: 'A long tab set',
      summary:
        'An app with more destinations than the bar should carry: the ones marked for overflow collapse into a ' +
        'trailing “More” menu, and the active surface living inside it still underlines the button.',
      placement: {
        component: 'caos-workspace-tabs',
        inputs: {
          homeLabel: 'Atlantis AI',
          activeAppLabel: 'Sales Atlas',
          tabs: [
            { id: 'o:account', label: 'Accounts', href: '/sales-atlas/account', object: 'account', icon: 'building-2' },
            { id: 'o:contact', label: 'Contacts', href: '/sales-atlas/contact', object: 'contact', icon: 'users' },
            { id: 'o:invoice', label: 'Invoices', href: '/sales-atlas/invoice', object: 'invoice', icon: 'file-text' },
            { id: 'o:order', label: 'Orders', href: '/sales-atlas/order', object: 'order', icon: 'file-text', overflow: true, active: true },
            { id: 'reports', label: 'Reports', href: '/sales-atlas/reports', icon: 'table-2', overflow: true },
          ],
        },
      },
      // Measured in a browser at 65px of menu below the button, which overhung the stage by 40. Being
      // unclippable is not the same as being visible: this menu is `position: fixed` against the
      // “More” button's live screen rectangle, so nothing CAN clip it — and on this page the thing it
      // lands on is the code panel underneath. Room is reserved for the same reason the other openers
      // reserve it, which is that a reader should be able to see the opened state and the code that
      // produced it at once.
      stage: { opensBelow: 48 },
    },
  ],
};

export default docs;
